import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Modal, Box, Typography, Button, Radio, RadioGroup,
    FormControlLabel, IconButton, Grid, Card, CardContent,
    TextField
} from "@mui/material";
import { Close } from "@mui/icons-material";
import "../../assets/styles/AddModal.scss";
import { CommonContext, TaskMasterContext } from "../../context";
import commonApi from "../../utils/api";
import AlertMessage from "../common/AlertMessage";
import { scrollToTop } from "../common/scrollToTop";

const initialFormState = {
    task_master: "",
    color_code: "#000000",
    status: "1"
};

const AddModal = () => {
    const { modalAlert, setModalAlert, setAlertMessage, setSeverity, user } = useContext(CommonContext);
    const {
        addModal,
        handleCloseModal,
        onLoadApi,
        viewData,
        isEdit,
        setIsLoading,
    } = useContext(TaskMasterContext);

    const [formDataTemp, setFormDataTemp] = useState(initialFormState);
    const [formErrors, setFormErrors] = useState({});
    const modalContentRef = useRef(null);

    const formatValue = (val) => {
        if (typeof val === 'object' && val !== null && val.type === 'Buffer' && Array.isArray(val.data)) {
            return String.fromCharCode.apply(null, val.data);
        }
        return val;
    };

    useEffect(() => {
        if (viewData && isEdit) {
            setFormDataTemp({
                id: formatValue(viewData.id),
                task_master: formatValue(viewData.task_master) || "",
                color_code: formatValue(viewData.color_code) || "#000000",
                status: viewData.status?.toString() || "1"
            });
        } else {
            setFormDataTemp(initialFormState);
        }
    }, [viewData, isEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormDataTemp((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formDataTemp.task_master) errors.task_master = "Task Master name is required";
        if (!formDataTemp.color_code) errors.color_code = "Color code is required";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        const url = isEdit ? "/users/task-master/edit" : "/users/task-master/add";
        const payload = {
            ...formDataTemp,
            admin_id: user?.id
        };

        try {
            const res = await commonApi.post(url, payload);
            scrollToTop(modalContentRef);
            setModalAlert(true);

            if (res?.data?.s === 1) {
                setAlertMessage(res?.data?.msg || "Success");
                setSeverity("success");
                setTimeout(() => {
                    handleCloseModal();
                    onLoadApi();
                }, 1500);
            } else {
                setAlertMessage(res?.data?.msg || "Something went wrong.");
                setSeverity("error");
            }
        } catch (err) {
            scrollToTop(modalContentRef);
            setModalAlert(true);
            setAlertMessage(err?.message || "Something went wrong.");
            setSeverity("error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={addModal} onClose={handleCloseModal}>
            <Box className="add-modal" sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '500px' },
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: '8px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Box className="modal-header" sx={{
                    p: 2,
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#004d40',
                    color: '#fff',
                    borderRadius: '8px 8px 0 0'
                }}>
                    <Typography variant="h6">
                        {isEdit ? "Edit Task Master" : "Add Task Master"}
                    </Typography>
                    <IconButton onClick={handleCloseModal} size="small" sx={{ color: '#fff' }}>
                        <Close />
                    </IconButton>
                </Box>

                <Box className="content-body" ref={modalContentRef} sx={{ p: 3, overflowY: 'auto' }}>
                    {modalAlert && <AlertMessage />}
                    <Grid container spacing={3}>
                        <Grid item size={{ xs: 12 }}>
                            <TextField
                                label="Task Master Name"
                                name="task_master"
                                value={formDataTemp.task_master}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                error={!!formErrors.task_master}
                                helperText={formErrors.task_master}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12 }}>
                            <TextField
                                label="Color Code"
                                name="color_code"
                                type="color"
                                value={formDataTemp.color_code}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                error={!!formErrors.color_code}
                                helperText={formErrors.color_code}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Status</Typography>
                            <RadioGroup
                                name="status"
                                value={formDataTemp.status}
                                onChange={handleChange}
                                sx={{ flexDirection: "row" }}
                            >
                                <FormControlLabel value="1" control={<Radio />} label="Active" />
                                <FormControlLabel value="0" control={<Radio />} label="Inactive" />
                            </RadioGroup>
                        </Grid>
                    </Grid>
                </Box>

                <Box className="form-actions" sx={{
                    p: 2,
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                }}>
                    <Button variant="outlined" onClick={handleCloseModal} color="error" size="small">
                        Cancel
                    </Button>
                    <Button variant="contained" color="success" onClick={handleSubmit} size="small">
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddModal;
